import { AreaTooltipContent, LegendItem } from '@/lib/Charts/common';
import colors from '@/tailwind/colors';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { HomeAudiencesChartItem } from './types';

type HomeAudiencesChartProps = {
  data: HomeAudiencesChartItem[];
};

export const HomeAudiencesChart: FC<HomeAudiencesChartProps> = ({ data }) => {
  const { t } = useTranslation();

  return (
    <>
      <LegendItem color='var(--brand-600)' name={t('home:thousands')} className='pe-7' />
      <ResponsiveContainer width='100%' height={207}>
        <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <defs>
            <filter id='whiteShadowLine' x='-10%' y='-50%' width='120%' height='200%'>
              <feFlood floodColor='#ffffff' result='whiteColor' />
              <feComposite in='whiteColor' in2='SourceGraphic' operator='in' result='whiteLine' />
              <feOffset in='whiteLine' dx='0' dy='5' result='whiteLineOffset' />
              <feComponentTransfer in='whiteLineOffset' result='thickWhiteLine'>
                <feFuncA type='discrete' tableValues='0 .2 .4 .6 .8 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1' />
              </feComponentTransfer>
              <feOffset in='whiteLineOffset' dx='0' dy='-2' result='whiteCopy1' />
              <feOffset in='whiteLineOffset' dx='0' dy='-1' result='whiteCopy2' />
              <feOffset in='whiteLineOffset' dx='0' dy='1' result='whiteCopy3' />
              <feOffset in='whiteLineOffset' dx='0' dy='2' result='whiteCopy4' />
              <feOffset in='whiteLineOffset' dx='0' dy='3' result='whiteCopy5' />
              <feOffset in='whiteLineOffset' dx='0' dy='4' result='whiteCopy6' />
              <feMerge result='thickWhiteLine'>
                <feMergeNode in='whiteCopy1' />
                <feMergeNode in='whiteCopy2' />
                <feMergeNode in='whiteLineOffset' />
                <feMergeNode in='whiteCopy3' />
                <feMergeNode in='whiteCopy4' />
                <feMergeNode in='whiteCopy5' />
                <feMergeNode in='whiteCopy6' />
              </feMerge>
              <feMerge>
                <feMergeNode in='thickWhiteLine' />
                <feMergeNode in='SourceGraphic' />
              </feMerge>
            </filter>
          </defs>

          <CartesianGrid strokeDasharray='none' stroke='var(--gray-300)' horizontal={true} vertical={false} />

          <XAxis
            dataKey='name'
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: '0.75rem', fill: colors.tertiary[600] }}
            dy={10}
            label={{
              value: t('home:months_other'),
              position: 'insideBottom',
              offset: -20,
              style: { textAnchor: 'middle', fontSize: '0.75rem', fill: colors.tertiary[600], fontWeight: '500' },
            }}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: '0.75rem', fill: colors.tertiary[600] }}
            tickCount={5}
            dx={-3}
            label={{
              value: t('home:amount'),
              angle: -90,
              offset: -10,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '0.75rem', fill: colors.tertiary[600], fontWeight: '500' },
            }}
          />

          <Tooltip content={AreaTooltipContent} />

          <Line
            dataKey='value'
            stroke='var(--brand-600)'
            strokeWidth={3}
            dot={false}
            activeDot={{
              r: 4,
              fill: 'var(--brand-600)',
              strokeWidth: 2,
              stroke: '#ffffff',
            }}
            style={{
              filter: 'url(#whiteShadowLine)',
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};
